import { supabase } from "@/lib/supabase";
import { callEdgeFunction } from "@/lib/supabaseAuth";
import type { IQuestInstructionsService } from "@/services/interfaces/IQuestInstructionsService";
import {
  isSafeUrl, normalizeDraft, validateDraft,
  type QuestInstructions, type QuestInstructionsAiDraft, type QuestInstructionsDraft, type QuestResource,
} from "@/lib/questInstructions";

interface Row {
  quest_id: string;
  instructions: string;
  resources: QuestResource[] | null;
  updated_at: string | null;
}

const COLUMNS = "quest_id, instructions, resources, updated_at";

function mapRow(row: Row): QuestInstructions {
  return {
    questId: row.quest_id,
    instructions: row.instructions ?? "",
    resources: Array.isArray(row.resources) ? row.resources : [],
    updatedAt: row.updated_at,
  };
}

const MIGRATION = "20260925130000_quest_instructions";

/**
 * True when the error means the quest_instructions table / RPC does not exist
 * yet — the frontend was deployed before the migration. PostgREST reports a
 * missing table as PGRST205 and a missing function as PGRST202; Postgres
 * itself uses 42P01 (undefined_table) and 42883 (undefined_function).
 */
export function isMissingSchemaError(error: { code?: string | null } | null | undefined): boolean {
  const code = error?.code ?? "";
  return code === "PGRST205" || code === "PGRST202" || code === "42P01" || code === "42883";
}

let warnedMissing = false;
function warnMissingOnce(): void {
  if (warnedMissing) return;
  warnedMissing = true;
  console.warn(
    `[questInstructions] quest_instructions is not in the database yet; apply migration ${MIGRATION}. ` +
      "Treating every quest as having no instructions until then.",
  );
}

/** Test-only: re-arm the one-time warning. */
export function resetMissingSchemaWarningForTests(): void {
  warnedMissing = false;
}

export class SupabaseQuestInstructionsService implements IQuestInstructionsService {
  async get(questId: string): Promise<QuestInstructions | null> {
    const { data, error } = await supabase
      .from("quest_instructions")
      .select(COLUMNS)
      .eq("quest_id", questId)
      .maybeSingle();
    if (error) {
      // Deployed ahead of the migration: behave as "no instructions" rather
      // than surfacing an error on every public quest page.
      if (isMissingSchemaError(error)) {
        warnMissingOnce();
        return null;
      }
      throw new Error(`Failed to load quest instructions: ${error.message}`);
    }
    return data ? mapRow(data as Row) : null;
  }

  async has(questId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("quest_has_instructions", { p_quest_id: questId });
    if (error) {
      if (isMissingSchemaError(error)) {
        warnMissingOnce();
        return false;
      }
      throw new Error(`Failed to check quest instructions: ${error.message}`);
    }
    return data === true;
  }

  async save(questId: string, draft: QuestInstructionsDraft): Promise<QuestInstructions> {
    const invalid = validateDraft(draft);
    if (invalid) throw new Error(invalid);
    const { data, error } = await supabase
      .from("quest_instructions")
      .upsert(
        {
          quest_id: questId,
          ...normalizeDraft(draft),
        },
        { onConflict: "quest_id" },
      )
      .select(COLUMNS)
      .single();
    if (error) throw new Error(error.message);
    return mapRow(data as Row);
  }

  async draft(questId: string, locale: string): Promise<QuestInstructionsAiDraft> {
    const res = await callEdgeFunction<{ instructions?: unknown; resources?: unknown; droppedLinks?: unknown }>(
      "draft-quest-instructions",
      { body: { questId, locale }, timeoutMs: 30_000 },
    );
    const resources = Array.isArray(res?.resources)
      ? (res.resources as QuestResource[]).filter(
          (r) => typeof r?.label === "string" && typeof r?.url === "string" && isSafeUrl(r.url.trim()),
        )
      : [];
    return {
      ...normalizeDraft({ instructions: typeof res?.instructions === "string" ? res.instructions : "", resources }),
      droppedLinks: typeof res?.droppedLinks === "number" ? res.droppedLinks : 0,
    };
  }
}
